import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/ui/Layout';
import { useAuth } from '../../components/context/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import { Container, Card, ListGroup, Spinner, Alert, Button, Row, Col, Badge, ButtonGroup, Dropdown } from 'react-bootstrap';
import { createLogger } from '../../utils/logger';

const logger = createLogger('OrderHistoryPage');

const statusOptions = [
  { key: 'all', label: 'All' },
  { key: 'packed_awaiting_pickup', label: 'Ready for Pickup' },
  { key: 'completed', label: 'Completed' },
  { key: 'awaiting_packing', label: 'Awaiting Packing' },
  { key: 'awaiting_payment_confirmation', label: 'Payment Processing' },
  { key: 'payment_successful', label: 'Payment Confirmed' },
  { key: 'payment_failed', label: 'Payment Failed' },
  { key: 'cancelled', label: 'Cancelled' }
];

const OrderHistoryPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderFilter, setOrderFilter] = useState('all');
  const [supermarketFilter, setSupermarketFilter] = useState('all');

  const breadcrumbItems = [
    { path: "/", label: "Home" },
    { label: "Order History" }
  ];

  const fetchOrderHistory = useCallback(async () => {
    if (!currentUser) {
      setError("Please log in to view your order history.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const ordersQuery = query(
        collection(db, 'Transactions'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(ordersQuery);
      const fetchedOrders = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setOrders(fetchedOrders);
    } catch (err) {
      logger.error("Error fetching order history:", err);
      setError(`Failed to load order history. ${  err.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchOrderHistory();
  }, [fetchOrderHistory]);

  const getStatusBadge = (status) => {
    let variant = 'secondary';
    let text = status ? status.replace(/_/g, ' ').replace(/\w\S*/g, (w) => w.replace(/^\w/, (c) => c.toUpperCase())) : 'Unknown';
    switch (status) {
        case 'initiated': variant = 'light'; text = 'Initiated'; break;
        case 'awaiting_payment_confirmation': variant = 'info'; text='Payment Processing'; break;
        case 'payment_successful': variant = 'primary'; text='Payment Confirmed'; break;
        case 'awaiting_packing': variant = 'warning'; text = 'Awaiting Packing'; break;
        case 'packed_awaiting_pickup': variant = 'success'; text = 'Ready for Pickup'; break;
        case 'completed': variant = 'dark'; text = 'Order Completed'; break;
        case 'cancelled_by_employee': variant = 'danger'; text='Cancelled'; break;
        case 'payment_cancelled_by_user': variant = 'danger'; text='Cancelled'; break;
        case 'payment_failed': variant='danger'; text='Payment Failed'; break;
        default: break;
    }
    return <Badge bg={variant} text={variant === 'light' || variant === 'warning' ? 'dark' : 'white'}>{text}</Badge>;
  };

  // Generate unique supermarket options for the dropdown
  const supermarketOptions = [
    { key: 'all', label: 'All hardware stores' },
    ...Array.from(new Set(orders.map(o => o.supermarketName).filter(Boolean)))
      .map(name => ({ key: name, label: name }))
  ];

  // Filter orders based on selected status and supermarket
  const filteredOrders = orders.filter(order => {
    const statusMatch =
      orderFilter === 'all' ? true :
      orderFilter === 'cancelled'
        ? ['cancelled_by_employee', 'payment_cancelled_by_user'].includes(order.status)
        : order.status === orderFilter;
    const supermarketMatch =
      supermarketFilter === 'all' ? true : order.supermarketName === supermarketFilter;
    return statusMatch && supermarketMatch;
  });

  if (loading) return <Layout><Container className="mt-4 text-center"><Spinner animation="border"/> <p>Loading order history...</p></Container></Layout>;
  
  return (
    <Layout>
      <Container className="mt-4">
        <Row className="align-items-center mb-1">
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={() => navigate(-1)} className="mb-2">
              &larr; Go Back
            </Button>
          </Col>
          <Col>
            <BreadcrumbNavigation items={breadcrumbItems} />
          </Col>
        </Row>
        <h2 className="mt-3 mb-3">Your Order History</h2>

        {/* Responsive filter controls */}
        <Row className="mb-3 g-2">
          <Col xs={12} md="auto">
            <div className="d-flex flex-wrap gap-2 justify-content-start">
              <ButtonGroup className="flex-wrap">
                {statusOptions.map(opt => (
                  <Button
                    key={opt.key}
                    variant={orderFilter === opt.key ? "primary" : "outline-primary"}
                    onClick={() => setOrderFilter(opt.key)}
                    className="mb-1"
                    style={{ minWidth: 120 }}
                  >
                    {opt.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          </Col>
          <Col xs={12} md="auto">
            <Dropdown onSelect={setSupermarketFilter}>
              <Dropdown.Toggle variant="outline-primary" id="dropdown-supermarket" className="w-100">
                {supermarketOptions?.find(opt => opt.key === supermarketFilter)?.label || 'All hardware stores'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {supermarketOptions?.map(opt => (
                  <Dropdown.Item key={opt.key} eventKey={opt.key} active={supermarketFilter === opt.key}>
                    {opt.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {!loading && filteredOrders.length === 0 && !error && (
          <Alert variant="info">No orders with this status.</Alert>
        )}

        {filteredOrders.map(order => (
          <Card key={order.id} className="mb-3 shadow-sm">
            <Card.Header className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
              <span className="mb-2 mb-md-0">Order ID: {order.id}</span>
              {getStatusBadge(order.status)}
            </Card.Header>
            <Card.Body>
              <Card.Title>{order.supermarketName}</Card.Title>
              <ListGroup variant="flush">
                <ListGroup.Item><strong>Date:</strong> {order.createdAt?.toDate().toLocaleString()}</ListGroup.Item>
                <ListGroup.Item><strong>Total Paid (Fee):</strong> ${Number(order.amountPaid ?? 0).toFixed(2)}</ListGroup.Item>
                <ListGroup.Item><strong>Subtotal (Due at Pickup):</strong> ${Number(order.orderSubtotal ?? 0).toFixed(2)}</ListGroup.Item>
                <ListGroup.Item><strong>Items:</strong> {order.cartItems?.length || 0}</ListGroup.Item>
              </ListGroup>
            </Card.Body>
            <Card.Footer>
              <Link to={`/order-status/${order.id}`} className="btn btn-primary btn-sm w-100 w-md-auto">View Details</Link>
            </Card.Footer>
          </Card>
        ))}
      </Container>
    </Layout>
  );
};

export default OrderHistoryPage;

// Add this to your CSS (or a global CSS file) for better mobile spacing:
/*
@media (max-width: 576px) {
  .btn-group.flex-wrap > .btn {
    min-width: 100px;
    font-size: 0.95em;
    margin-bottom: 4px;
  }
  .card .btn {
    width: 100%;
    margin-bottom: 4px;
  }
  .card-header, .card-footer {
    flex-direction: column !important;
    align-items: flex-start !important;
  }
}
*/
